import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import Map from './Map'
import { useMemberMap } from 'hooks/useMemberMap'
import Loader from 'react-loader-spinner'
import styled from 'styled-components'
import theme from '../../themes/default'
import LeftPanel from './LeftPanel'
import { useQueryParams } from 'raviger'
import { isEmpty } from 'lodash'

const defaultCenter = { lat: 39.8283, lng: -98.5795 }
const defaultZoom = 4

const MemberPagPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [mapOrganizations, setMapOrganizations] = useState([])
  const [filteredOrganizations, setFilteredOrganizations] = useState([])
  const [center, setCenter] = useState(defaultCenter)
  const [zoom, setZoom] = useState(defaultZoom)
  
  const [queryParams] = useQueryParams()
  const { orgId, orgName, lat: latitude, lng: longitude, isMember } = queryParams
  const [showNonMembers, setShowNonMembers] = useState(isMember === 'false')

  useEffect(() => {
    if (orgId && orgName && latitude && longitude) {
      handleOnSelect({ latitude, longitude, id: +orgId })
      setSearchTerm(decodeURIComponent(orgName))
    }
  }, [orgId, orgName, latitude, longitude])

  const { data, loading } = useMemberMap()

  useEffect(() => {
    if (data) {
      const orgs = data.data.filter(
        ({ latitude, longitude, type }) => latitude && longitude
      )
      setOrganizations(orgs)
      setFilteredOrganizations(orgs)
      setMapOrganizations(orgs)
    }
  }, [data])

  const handleMarkerClick = (org) => {
    setSelectedOrg(org)
  }

  const handleInfoClose = () => {
    setSelectedOrg(null)
  }

  const handleOnSelect = ({ latitude, longitude, id }) => {
    setSelectedOrg(id)
    //Center map on selected org
    setCenter({ lat: +latitude, lng: +longitude })
    setZoom(12)
  }

  const handleOnFilter = (searchTerm) => {
    setSearchTerm(searchTerm)
    setFilteredOrganizations(
      organizations.filter((organization) =>
        organization.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
  }

  const handleTypeChange = (selectedTypes) => {
    setMapOrganizations(
      organizations.filter((organization) =>
        selectedTypes.includes(organization.type)
      )
    )
  }

  const handleReset = useCallback(() => {
    setMapOrganizations(organizations)
  }, [organizations])

  const handleOnShowNonMembers = (value) => {
    setShowNonMembers(value)
  }

  const handleOnClear = () => {
    handleOnFilter('')
    setSelectedOrg(null)
    setSearchTerm('')
    setZoom(defaultZoom)
    setCenter(defaultCenter)
    handleOnShowNonMembers(false)
  }

  return (
    <Container fluid>
      {loading ? (
        <Spinner>
          <Loader
            type="Oval"
            height={30}
            color={theme.palette.primary[0]}
            timeout={10000}
          />
        </Spinner>
      ) : (
        <>
          {!isEmpty(organizations) && (
            <Row>
              <Col md="2">
                <LeftPanel
                  organizations={filteredOrganizations}
                  onSelect={handleOnSelect}
                  onFilter={handleOnFilter}
                  onTypeChange={handleTypeChange}
                  onReset={handleReset}
                  showNonMembers={showNonMembers}
                  onShowNonMembers={handleOnShowNonMembers}
                  orgName={searchTerm ?? orgName}
                  onClear={handleOnClear}
                />
              </Col>
              <Col>
                <Map
                  organizations={mapOrganizations}
                  selectedOrg={selectedOrg}
                  onMarkerClick={handleMarkerClick}
                  onInfoClose={handleInfoClose}
                  containerElement={<div style={{ height: '400px' }} />}
                  mapElement={<div style={{ height: '70vh' }} />}
                  center={center}
                  zoom={zoom}
                  showNonMembers={showNonMembers}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  )
}

const Spinner = styled.div`
  text-align: center;
`

export default MemberPagPage
